<template>
  <form class="needs-validation ml-0" @submit.prevent="completeForm" novalidate>
    <div class="col px-0 pr-lg-3">
      <section class="bg-white rounded mb-4 p-3 p-sm-4">
        <div class="d-flex justify-content-between align-items-start mb-4">
          <h1 class="h2 mb-3">{{ edit ? 'Edit' : 'Create' }} Draft</h1>
        </div>
        <div role="alert" class="alert alert-warning" v-if="errors">{{errors}}</div>
        <div class="alert alert-success" v-if="success">{{success}}</div>
        <div class="row align-items-start">
          <div class="col-12 col-md-6 form-group row no-gutters pr-xl-4">
            <label for="draftName">Draft Name</label>
            <input type="text" class="form-control" id="draftName" required v-model="draftDetails.name">
            <div class="invalid-tooltip">Please include the Draft Name.</div>
          </div>
          <div class="col-12 col-md-6 pl-xl-4 form-group" v-if="Object.keys(listCommittees).length > 0">
            <label for="committeeName">Committee</label>
            <div class="form-inline align-items-start">
              <tags-input element-id="committeeName"
                v-model="selectedCommittee"
                :existing-tags="listCommittees"
                :onlyExistingTags="true"
                :addTagsOnComma="true"
                :typeahead="true"
                :typeaheadStyle="'dropdown'"
                :typeaheadMaxResults="20"
                :typeaheadActivationThreshold="2"
                :limit="1"
                :placeholder="'Start Typing...'"
                ref="tagsInput"></tags-input>
              <div class="invalid-tooltip" :class="{ 'd-block': committeeIsInvalid }">Please include the Committee Name.</div>
            </div>
          </div>
          <div class="col-12 col-md-6 pl-xl-4 form-group align-self-end" v-else>
            <div class="text-center py-2 m-auto">
              <div class="spinner-border text-primary" role="status">
                <span class="sr-only">Loading...</span>
              </div>
            </div>
          </div>

          <div class="col-12 col-xl-6 pr-xl-4 form-group">
            <label for="draftFrom" class="d-none d-xl-block">Date</label>
            <div class="row align-items-start">
              <div class="col-12 col-md-6 mb-4 mb-md-2 mb-xl-0 pr-xl-1">
                <label for="draftFrom" class="d-xl-none">Date <small>(from)</small></label>
                <input type="date" :class="['form-control form-date', errorStatus === 'validation_error' && errors ? 'is-invalid' : '']" id="draftFrom" required v-model="draftDetails.start_date" :max="draftDetails.end_date" :placeholder="[isLegacy() ? 'yyyy-mm-dd' : 'dd/mm/yyyy']" pattern="[0-9]{4}-[0-9]{2}-[0-9]{2}">
                <div class="invalid-tooltip">Please include the Start Date{{ isLegacy() ? ' in the format yyyy-mm-dd' : ''}}.</div>
              </div>

              <div class="col-12 col-md-6 mb-xl-0 pl-xl-1">
                <label for="draftFrom" class="d-xl-none">Date <small>(to)</small></label>
                <input type="date" :class="['form-control form-date', errorStatus === 'validation_error' && errors ? 'is-invalid' : '']" id="draftTo" required v-model="draftDetails.end_date" :min="draftDetails.start_date" :placeholder="[isLegacy() ? 'yyyy-mm-dd' : 'dd/mm/yyyy']" pattern="[0-9]{4}-[0-9]{2}-[0-9]{2}">
                <div class="invalid-tooltip">Please include the End Date{{ isLegacy() ? ' in the format yyyy-mm-dd' : ''}}.</div>
              </div>

            </div>
          </div>

          <div class="col-12 col-md-6 pl-xl-4 form-group">
            <label for="zone">Zone</label>
            <select class="form-control" id="zone" required v-model="draftDetails.zone_id" :disabled="zoneFieldDisabled">
              <option disabled></option>
              <option v-for="(zone, index) in $store.state.listZones" :key="index" :value="zone.id">{{zone.code}}</option>
            </select>
            <div class="invalid-tooltip">Please include the Zone.</div>
          </div>

          <div class="col-12 col-md-6 pr-xl-4 form-group">
            <label for="status">Status</label>
            <select class="form-control" id="status" v-model="draftDetails.draft_status_id" required>
              <option disabled></option>
              <option v-for="(status, index) in $store.getters.getDraftStatusesAdmin" :key="index" :value="status.id">{{ status.nameAdmin || status.name }}</option>
            </select>
            <div class="invalid-tooltip">Please include the Status.</div>
          </div>

          <div class="col-12 col-md-6 pl-xl-4 form-group">
            <label for="program">Program</label>
            <div class="custom-file">
              <input type="file" :class="['custom-file-input font-weight-normal', { 'is-invalid': errorStatus === 'filetype_pdf' || errorStatus === 'filesize_pdf' }]" id="program" accept="application/pdf" @change="fileUpload">
              <label class="custom-file-label font-weight-normal"
                for="program">
                {{ this.temporaryFileData.name ? this.temporaryFileData.name : this.draftDetails.attachment ? this.draftDetails.attachment : 'Choose file...'}}
              </label>
              <div class="invalid-tooltip" v-if="errorStatus === 'filesize_pdf'">File is too large. Maximum allowed size is 3MB.</div>
              <div class="invalid-tooltip" v-else>Please select a PDF file.</div>
              <small>PDF file type required.</small>
              <button
                v-if="this.draftDetails.filename"
                type="button"
                class="btn btn-link px-2 pl-md-0 px-xl-2 pr-md-1"
                @click="deleteAttachment"
              >
                <span class="sr-only">Delete Program</span>
                <div class="btn-remove">
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" aria-hidden="true" viewBox="0 0 17 17"><g fill="currentColor"><path d="M8.5 17C3.8 17 0 13.2 0 8.5S3.8 0 8.5 0 17 3.8 17 8.5 13.2 17 8.5 17zm0-16C4.4 1 1 4.4 1 8.5S4.4 16 8.5 16 16 12.6 16 8.5 12.6 1 8.5 1z"/><path d="M10.6 11.1c-.1 0-.3 0-.4-.1L6 6.8c-.2-.2-.2-.5 0-.7s.5-.2.7 0l4.2 4.2c.2.2.2.5 0 .7-.1 0-.2.1-.3.1z"/><path d="M6.4 11.1c-.1 0-.3 0-.4-.1-.2-.2-.2-.5 0-.7l4.2-4.2c.2-.2.5-.2.7 0s.2.5 0 .7L6.7 11s-.2.1-.3.1z"/></g></svg>
                </div>
              </button>
            </div>
            <div class="attached-file-wrapper ml-2 mb-2" v-if="this.draftDetails.attachment && this.draftDetails.filename" id="secretaryProgram">
              {{ this.draftDetails.filename }}
              <div class="btn-download-file" @click="downloadProgram(draftDetails.id, 'attachment')">
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" aria-hidden="true" viewBox="0 0 17 17">
                  <g fill="currentColor">
                    <path
                      d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"></path>
                    <path
                      d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"></path>
                  </g>
                </svg>
              </div>
            </div>
          </div>

          <div class="col-12 col-md-6 pr-xl-4 form-group">
            <label for="blogSource">Blog source</label>
            <input type="text" class="form-control" id="blogSource"
                   :class="{ 'is-invalid': $v.draftDetails.blog_source.$error}"
                   v-model.trim="$v.draftDetails.blog_source.$model" autocomplete="off">
            <div class="invalid-tooltip" v-if="!$v.draftDetails.blog_source.url">Please include correct source address.</div>
          </div>

          <div class="col-12 col-md-6 pr-xl-4 form-group" v-if="this.secretary_program && this.secretary_program.filename">
            <label class="ml-xl-2">Secretary program</label>

            <div class="attached-file-wrapper ml-2 mb-2" v-if="this.secretary_program && this.secretary_program.filename" id="secretaryProgram">
              {{ this.secretary_program.filename }}
              <div class="btn-download-file" @click="downloadProgram(draftDetails.id, 'secretary_program')">
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" aria-hidden="true" viewBox="0 0 17 17">
                  <g fill="currentColor">
                    <path
                      d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"></path>
                    <path
                      d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"></path>
                  </g>
                </svg>
              </div>
            </div>
            <button
              v-if="this.secretary_program && this.secretary_program.filename"
              type="button"
              class="btn btn-link px-2 pl-md-0 px-xl-2 pr-md-1"
              @click="approveSecretaryProgram(draftDetails.id)">
              <span class="sr-only">Approve secretary program</span>
              <div class="btn btn-block px-md-3 btn-primary">
                Approve program
              </div>
            </button>
          </div>

          <div class="col-12 align-self-end form-group">
            <div class="custom-control custom-checkbox">
              <input type="checkbox" v-model="draftDetails.is_final" class="custom-control-input" id="is_final">
              <label class="custom-control-label font-weight-normal h6 mb-0" for="is_final">Yes, this is the National Finals Draft</label>
            </div>
          </div>

          <div class="col-12 align-self-end form-group">
            <div class="custom-control custom-checkbox">
              <input type="checkbox" v-model="draftDetails.is_futurity" class="custom-control-input" id="is_futurity">
              <label class="custom-control-label font-weight-normal h6 mb-0" for="is_futurity">Yes, there is a Futurity on this program</label>
            </div>
          </div>

          <div class="col-12 d-lg-none mt-4">
            <button class="btn btn-block-mob btn-primary" type="submit" @click="handleSubmit">{{ edit ? 'Update' : 'Create' }}</button>
          </div>
        </div>
      </section>
    </div>

    <desktop-action-bar>
      <button :class="['btn btn-block px-md-5', processing ? 'btn-primary-dark' : 'btn-primary']" type="submit" @click="handleSubmit">
        {{ !processing ? edit ? 'Update' : 'Create' : '' }}
        <div v-if="processing" class="spinner-border text-light" role="status"><span class="sr-only">Loading...</span></div>
      </button>
    </desktop-action-bar>

  </form>
</template>

<script>
import axios from 'axios'
import HANDLESUBMIT from '@/mixins/formValidation.js'
import LEGACYBROWSERS from '@/mixins/legacyBrowsers.js'
import VoerroTagsInput from '@voerro/vue-tagsinput'
import Vue from "vue"
import Vuelidate from "vuelidate"
import { url } from "vuelidate/lib/validators"
import DesktopActionBar from '@/components/DesktopActionBar'

Vue.use(Vuelidate)

export default {
  name: 'DraftCreateDetails',
  props: ['details', 'edit'],
  mixins: [HANDLESUBMIT, LEGACYBROWSERS],
  components: {
    'tags-input': VoerroTagsInput,
    DesktopActionBar
  },
  data() {
    return {
      draftDetails: this.details ? { ...this.details } : { attachment: '', blog_source: '', zone_id: null },
      fileName: '',
      listCommittees: this.$store.getters.getCommittees,
      selectedCommittee: [],
      errorStatus: '',
      errors: '',
      processing: false,
      zoneFieldDisabled: true,
      committeeIsInvalid: false,
      secretary_program: null,
      success: null,
      temporaryFileData: { name: null }
    }
  },
  validations() {
    return {
      draftDetails: {
        blog_source: {
          url
        }
      }
    }
  },
  computed: {
    listCommitteesCount() {
      return this.$store.state.listCommittees.length
    }
  },
  watch: {
    selectedCommittee() {
      this.draftDetails.committee_id = Number(this.selectedCommittee.toString())
      if (this.selectedCommittee.length !== 0 && this.draftDetails.committee_id !== undefined) {
        this.committeeIsInvalid = false

        if (this.draftDetails?.committee_id && !this.draftDetails.id) {
          const committee = this.$store.getters.getCommittee(this.draftDetails.committee_id)
          this.draftDetails.zone_id = committee?.zone_id ? committee.zone_id : null
        }
      } else {
        this.committeeIsInvalid = true
      }
    },
    listCommitteesCount() {
      this.listCommittees = this.$store.getters.getCommittees
    }
  },
  methods: {
    isInvalid() {
      if (this.wasValidated) {
        if (this.draftDetails.committee_id === undefined) {
          return true
        } else if (this.draftDetails.committee_id?.length === 0) {
          return true
        }
      }
      return false
    },
    completeForm() {
      if (this.selectedCommittee.length === 0) {
        this.committeeIsInvalid = true
      }
      setTimeout(() => {
        if (this.formComplete && this.draftDetails.committee_id) {
          this.addDraft()
        }
      }, 10)
    },
    addDraft() {
      const formData = new FormData()
      formData.append('file', this.draftDetails.attachment)
      formData.append('fields', JSON.stringify(this.draftDetails))
      this.processing = true
      axios.post(`${this.$api}/admin/draft-edit`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }).then((response) => {
        // eslint-disable-next-line
        // console.log(response)
        this.processing = false
        if (response.data.status === 'success') {
          this.$router.push({ name: 'Drafts', params: { success: `Great! Your draft has been successfully ${this.edit ? 'edited' : 'created'}. You can now begin adding events.` } })
        } else if (response.data.status === 'validation_error' && response.data.errors.zone_id_changed) {
          // Zone Id Changed
          this.success = ''
          this.errorStatus = 'zone_id_changed'
          this.errors = `The Zone cannot be changed for drafts that have event results.`
        } else if (response.data.status === 'validation_error' && response.data.errors.blog_source) {
          // Blog Source
          this.success = ''
          this.errorStatus = 'blog_source'
          this.errors = `The blog source is invalid.`
        } else {
          // Error
          this.success = ''
          this.errors = 'Please check for errors & omissions.'
        }
      }).catch((error) => {
        // eslint-disable-next-line
        console.log(error)
        this.processing = false
        this.error = 'Unable to receive data from the server. Please reload the page.'
      })
    },
    deleteDraft() {

    },
    fileUpload(event) {
      const uploadedFile = event.target.files[0]
      if (!uploadedFile.name.endsWith('.pdf')) {
        this.deleteAttachment()
        this.errorStatus = 'filetype_pdf'
      } else if (uploadedFile.size > 3 * 1024 * 1024) {
        this.deleteAttachment()
        this.errorStatus = 'filesize_pdf'
      } else {
        this.draftDetails.attachment = uploadedFile
        // this.fileName = uploadedFile.name
        this.temporaryFileData.name = uploadedFile.name
        this.errorStatus = null
      }
    },
    deleteAttachment() {
      this.draftDetails.attachment = null
      this.draftDetails.fileName = null
    },
    downloadProgram(id, field) {
      if (id) {
        axios.get(`${this.$api}/admin/drafts/download-program/${id}/${field}`).then((response) => {
          window.open(response.data.path, '_blank')
        })
      }
    },
    approveSecretaryProgram(id) {
      if (id) {
        axios.post(`${this.$api}/admin/drafts/approve-program/${id}`).then((response) => {
          if (response.data.status === 'success') {
            this.draftDetails.attachment = this.secretary_program.filepath
            this.draftDetails.filename = this.secretary_program.filename
            this.secretary_program = null
            this.success = 'Secretary program successfully approved. It will be published on the website https://www.campdraft.com.au'
          }
        })
      }
    }
  },
  mounted() {
    // eslint-disable-next-line eqeqeq
    const draftZoneIsNull = !this.draftDetails.zone_id
    if (draftZoneIsNull) {
      this.zoneFieldDisabled = false
    }
    /* Get owners list */
    this.$nextTick(() => {
      if (this.details && this.details.committee_id) {
        this.selectedCommittee.splice(0, 1, this.details.committee_id.toString())
      }
      if (this.details && this.details.id) {
        axios.get(`${this.$api}/admin/draft-details/${this.details.id}/`).then((response) => {
          // eslint-disable-next-line
          // console.log(response)
          if (response.data.status === 'success') {
            this.secretary_program = response.data.data.secretary_program
            this.draftDetails = response.data.data
          } else {
            this.error = 'Unable to receive data from the server. Please reload the page.'
          }
        })
      }
    })
  }
}
</script>




// WEBPACK FOOTER //
// src/views/Drafts/Add/DraftAddDetails.vue